import { Banknote, ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Card } from '@/components/ui/Card'
import { useDashboard } from '@/features/dashboard/api/useDashboard'
import { TO_PAY_HREF } from '@/features/tasks/taskHref'
import { cn } from '@/lib/cn'
import { formatNumber } from '@/lib/format'

/* «К оплате» on «Мои задачи» — ONE LINE with a link (S-2b.2, owner decision Q4).
 *
 * The figure is the dashboard's own `counters.toPay`, read through
 * `useDashboard`, not a second rule: it is not on the tasks wire at all (B2),
 * because a missing FX rate (AD-20) would then fail the whole task read, and
 * the heaviest read the server has would run twice.
 *
 * MOUNT IT ONLY FOR `can(user, CAP.financeReceivablesRead)` — the guard on
 * `GET /api/dashboard`. The gate is the mount, not an `enabled` flag, so a
 * reader without the capability makes no dashboard request at all.
 *
 * CounterStrip's rule holds: a figure the response lacks — still loading,
 * withheld because its read failed, or named in `pending` — shows a muted «—»,
 * never 0. A zero reads "nothing to pay", and Finance who believes that stops
 * chasing invoices.
 *
 * The link is "go and act" (B1): the balances over every period, where the
 * payables are worked. It is not a drill-through and does not promise a list
 * that sums to the figure — the dashboard's `TILE_HREFS` holds that rule. */
export function ToPayLine() {
  const { t } = useTranslation()
  const { data } = useDashboard()
  /* Resolved once, so the value and its unit cannot disagree about whether
     there is a figure — a "USD" trailing an em dash is the shape that bug
     takes. */
  const toPay =
    data && !data.pending.some((p) => p.key === 'toPay') ? data.counters.toPay : undefined

  return (
    <Card>
      <Link
        to={TO_PAY_HREF}
        className={cn(
          'flex items-center gap-3 px-[18px] py-3.5 no-underline',
          'hover:bg-surface-hover transition-colors',
          'duration-[var(--dur-instant)] ease-[var(--ease-standard)]',
          'hover:no-underline',
        )}
      >
        <Banknote size={16} className="text-text-muted shrink-0" aria-hidden />
        <span className="text-text-heading min-w-0 truncate text-[13px] font-normal">
          {t('myTasks.toPay.label')}
        </span>
        <span className="flex-1" />
        {/* A computed total: one of the four sanctioned uses of weight 600.
            Only the digits are tabular. */}
        <span
          className={cn(
            'text-[15px] leading-none whitespace-nowrap',
            toPay ? 'text-text-heading font-semibold' : 'text-text-muted font-normal',
          )}
        >
          <span className="asa-num">{toPay ? formatNumber(toPay.amount, 2) : '—'}</span>
          {toPay ? (
            <span className="text-text-muted ml-1 text-[12px] font-normal">
              {toPay.currency}
            </span>
          ) : null}
        </span>
        <span className="text-text-link hidden text-[12.5px] font-normal whitespace-nowrap sm:inline">
          {t('myTasks.toPay.open')}
        </span>
        <ChevronRight size={16} className="text-text-muted shrink-0" aria-hidden />
      </Link>
    </Card>
  )
}
